// Product option groups on the product page: which groups apply, what is selected, what it costs.
// The rules mirror the server (astro-backend/src/services/orderItemPricing.js), which is the
// authority on price: the page only shows what the server will charge and only sends what it accepts.
//
// A selection is { [groupId]: [choiceId, ...] }.
//
// Conventions (see the backend):
//  - A choice label may be a path with " › " ("Ring › Silver › RD1"). Such a group is shown as
//    steps, and the group name gives the step titles ("Style › Metal › Design").
//  - A group may carry showWhenGroup + showWhenPath: it applies only when the selected choice of
//    the group with that name is that path, or sits under it.

export const SEP = " › ";

const norm = (text) => String(text ?? "").trim().toLowerCase();
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const splitPath = (text) => String(text ?? "").split(SEP).map((s) => s.trim());

// "Ring" matches "Ring" and "Ring › Silver › RD1", never "Ringlet". An empty path matches anything.
export const isUnderPath = (label, path) => {
  const wanted = norm(path);
  if (!wanted) return true;
  const have = norm(label);
  return have === wanted || have.startsWith(`${wanted}${SEP.toLowerCase()}`);
};

// Groups and choices in display order, with numeric prices. Ties keep their creation order.
export const normalizeGroups = (optionGroups) =>
  (Array.isArray(optionGroups) ? optionGroups : [])
    .map((g, index) => ({
      ...g,
      _index: index,
      choices: (Array.isArray(g.choices) ? g.choices : [])
        .map((c, i) => ({ ...c, priceDelta: Number(c.priceDelta) || 0, _index: i }))
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a._index - b._index),
    }))
    .filter((g) => g.choices.length > 0)
    .sort(
      (a, b) =>
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
        String(a.createdAt || "").localeCompare(String(b.createdAt || "")) ||
        a._index - b._index
    );

const selectedIdsOf = (selection) => new Set(Object.values(selection || {}).flat());

// Ids of the groups that apply for this selection (a conditional group applies once a group that
// itself applies has the matching choice selected, so chains work).
export const activeGroupIds = (groups, selection) => {
  const selected = selectedIdsOf(selection);
  const active = new Set(groups.filter((g) => !g.showWhenGroup).map((g) => g.id));
  let changed = true;
  while (changed) {
    changed = false;
    for (const group of groups) {
      if (active.has(group.id)) continue;
      const applies = groups.some(
        (parent) =>
          parent.id !== group.id &&
          active.has(parent.id) &&
          norm(parent.name) === norm(group.showWhenGroup) &&
          parent.choices.some((c) => selected.has(c.id) && isUnderPath(c.label, group.showWhenPath))
      );
      if (applies) {
        active.add(group.id);
        changed = true;
      }
    }
  }
  return active;
};

// Drops anything that no longer applies: choices of groups that are now hidden, unknown ids, and
// more than one choice in a single-choice group.
export const cleanSelection = (groups, selection) => {
  // repeat so that hiding one group also drops what depended on it
  let current = selection || {};
  for (let pass = 0; pass <= groups.length; pass += 1) {
    const active = activeGroupIds(groups, current);
    const next = {};
    for (const group of groups) {
      if (!active.has(group.id)) continue;
      const valid = new Set(group.choices.map((c) => c.id));
      let ids = (current[group.id] || []).filter((id) => valid.has(id));
      if (group.selectionType === "single") ids = ids.slice(0, 1);
      if (ids.length) next[group.id] = ids;
    }
    if (JSON.stringify(next) === JSON.stringify(current)) return next;
    current = next;
  }
  return current;
};

export const isTreeGroup = (group) => group.choices.some((c) => String(c.label).includes(SEP));

// What a first visit starts with: a required step-by-step group opens on its first top-level choice
// when that is a free single-step choice (the shop's "Loose"), as the page always did.
export const defaultSelection = (groups) => {
  const selection = {};
  for (const group of groups) {
    const first = group.choices[0];
    if (group.isRequired && group.selectionType === "single" && isTreeGroup(group) && first && !first.label.includes(SEP) && first.priceDelta === 0) {
      selection[group.id] = [first.id];
    }
  }
  return cleanSelection(groups, selection);
};

// ----------------------------------------------------------------- reading a selection

const chosenChoices = (groups, selection) => {
  const active = activeGroupIds(groups, selection);
  const picked = [];
  for (const group of groups) {
    if (!active.has(group.id)) continue;
    const ids = new Set(selection?.[group.id] || []);
    group.choices.forEach((choice) => {
      if (ids.has(choice.id)) picked.push({ group, choice });
    });
  }
  return picked;
};

// Total added to the price of ONE piece by the selected options.
export const optionsPrice = (groups, selection) =>
  round2(chosenChoices(groups, selection).reduce((sum, { choice }) => sum + choice.priceDelta, 0));

// The ids to send with the order (only groups that apply).
export const selectedChoiceIds = (groups, selection) => chosenChoices(groups, selection).map(({ choice }) => choice.id);

// Names of groups that apply, are required, and have nothing selected.
export const missingRequired = (groups, selection) => {
  const active = activeGroupIds(groups, selection);
  return groups
    .filter((g) => active.has(g.id) && g.isRequired && !(selection?.[g.id] || []).length)
    .map((g) => g.name);
};

// One line per selected choice, for the price summary and the order record.
export const describeSelection = (groups, selection) =>
  chosenChoices(groups, selection).map(({ group, choice }) => ({
    groupName: group.name,
    label: choice.label,
    priceDelta: choice.priceDelta,
    isPath: String(choice.label).includes(SEP),
  }));

// "Ring › Silver › RD1 • Ring size: 6 • Lab certificate: General certificate, X-ray certificate"
export const optionsLabel = (groups, selection) => {
  const parts = [];
  const byGroup = new Map();
  describeSelection(groups, selection).forEach((line) => {
    const list = byGroup.get(line.groupName) || [];
    list.push(line);
    byGroup.set(line.groupName, list);
  });
  byGroup.forEach((lines, groupName) => {
    const text = lines.map((l) => l.label).join(", ");
    parts.push(lines[0].isPath ? text : `${groupName}: ${text}`);
  });
  return parts.join(" • ");
};

// ------------------------------------------------------------------ step-by-step groups

// Titles of the steps of a path group, from its name ("Style › Metal › Design").
export const stepTitles = (group) => splitPath(group.name);

// The options of one step, given the steps already chosen. Each option is a segment; it is a `leaf`
// (a real choice, selecting it completes the group) or has deeper steps. `image` is the first picture
// found under it, so a step's cards can show one.
export const stepOptions = (group, chosenPath) => {
  const depth = chosenPath.length;
  const under = group.choices.filter((c) => {
    const parts = splitPath(c.label);
    return chosenPath.every((seg, i) => norm(parts[i]) === norm(seg));
  });

  const options = [];
  const seen = new Map();
  for (const choice of under) {
    const parts = splitPath(choice.label);
    const segment = parts[depth];
    if (segment === undefined) continue;
    const key = norm(segment);
    if (!seen.has(key)) {
      const option = { segment, leaf: null, image: "" };
      seen.set(key, option);
      options.push(option);
    }
    const option = seen.get(key);
    if (parts.length === depth + 1) option.leaf = choice;
    if (!option.image && choice.imageUrl) option.image = choice.imageUrl;
  }
  return options;
};

// The steps already taken to reach a selected choice.
export const pathOfChoice = (group, choiceId) => {
  const choice = group.choices.find((c) => c.id === choiceId);
  return choice ? splitPath(choice.label) : [];
};
